import axios, { AxiosError } from "axios";
import {
  ApiResponse,
  AuthResponse,
  RefreshResponse,
  DashboardStats,
  GrowthAnalytics,
  PlatformAnalytics,
  SystemHealth,
  User,
  PaginatedResponse,
  PetAnalyticsData,
  UserEngagementAnalytics,
  LostPet,
  LostPetStats,
} from "../types";
import { tokenStorage } from "./tokenStorage";
import { getApiConfig, getCurrentEnvConfig } from "../config/api";

const config = getApiConfig();
const envConfig = getCurrentEnvConfig();
const API_BASE_URL = config.baseURL;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: envConfig.timeout,
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and refresh token logic
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenStorage.getRefreshToken();
      
      if (!refreshToken) {
        // No refresh token available, redirect to login
        tokenStorage.clearTokens();
        processQueue(error, null);
        window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        // Attempt to refresh the token
        const response = await axios.post<RefreshResponse>(
          `${API_BASE_URL}${config.endpoints.auth.refresh}`,
          { refreshToken }
        );

        if (response.data.success) {
          const { accessToken } = response.data;
          
          // Update the stored access token
          tokenStorage.updateAccessToken(accessToken);
          
          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          
          // Process queued requests
          processQueue(null, accessToken);
          
          // Retry the original request
          return api(originalRequest);
        } else {
          throw new Error('Refresh failed');
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        tokenStorage.clearTokens();
        processQueue(refreshError, null);
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);


export const adminApi = {
  // Authentication
  login: async (email: string, password: string): Promise<AuthResponse> => {
    console.log("login", email, password);
    const response = await api.post<AuthResponse>(config.endpoints.auth.login, { email, password });
    return response.data;
  },

  // Refresh token
  refreshToken: async (refreshToken: string): Promise<RefreshResponse> => {
    const response = await api.post<RefreshResponse>(config.endpoints.auth.refresh, { refreshToken });
    return response.data;
  },

  // Logout
  logout: async (): Promise<void> => {
    const refreshToken = tokenStorage.getRefreshToken();
    if (refreshToken) {
      try {
        await api.post(config.endpoints.auth.logout, { refreshToken });
      } catch (error) {
        console.error('Logout request failed:', error);
      }
    }
    tokenStorage.clearTokens();
  },

  // Dashboard Stats
  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await api.get<ApiResponse<DashboardStats>>(
      config.endpoints.admin.dashboard
    );
    return response.data.data;
  },

  // Growth Analytics
  getGrowthAnalytics: async (
    period: string = "30d"
  ): Promise<GrowthAnalytics> => {
    const response = await api.get<ApiResponse<GrowthAnalytics>>(
      `${config.endpoints.admin.analytics.growth}?period=${period}`
    );
    return response.data.data;
  },

  // Platform Analytics
  getPlatformAnalytics: async (): Promise<PlatformAnalytics> => {
    const response = await api.get<ApiResponse<PlatformAnalytics>>(
      config.endpoints.admin.analytics.platform
    );
    return response.data.data;
  },

  // Pet Analytics
  getPetAnalytics: async (): Promise<PetAnalyticsData> => {
    const response = await api.get<ApiResponse<PetAnalyticsData>>(
      config.endpoints.admin.analytics.pets
    );
    return response.data.data;
  },

  // User Engagement Analytics
  getUserEngagementAnalytics: async (): Promise<UserEngagementAnalytics> => {
    const response = await api.get<ApiResponse<UserEngagementAnalytics>>(
      config.endpoints.admin.analytics.engagement
    );
    return response.data.data;
  },

  // Messaging Analytics
  getMessagingAnalytics: async (): Promise<any> => {
    const response = await api.get<ApiResponse<any>>(
      config.endpoints.admin.analytics.messaging
    );
    return response.data.data;
  },

  // User Management
  getUsers: async (
    params: {
      page?: number;
      limit?: number;
      search?: string;
      platform?: string;
      sortBy?: string;
      sortOrder?: string;
    } = {}
  ): Promise<PaginatedResponse<User>> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await api.get<ApiResponse<PaginatedResponse<User>>>(
      `${config.endpoints.admin.users}?${queryParams}`
    );
    return response.data.data;
  },

  // System Health
  getSystemHealth: async (): Promise<SystemHealth> => {
    const response = await api.get<ApiResponse<SystemHealth>>(
      config.endpoints.admin.system.health
    );
    return response.data.data;
  },

  // Lost Pets
  getLostPets: async (
    page: number = 1,
    limit: number = 10,
    status?: string,
    lostPetStatus?: string
  ): Promise<PaginatedResponse<LostPet>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (status) {
      params.append('status', status);
    }
    
    if (lostPetStatus) {
      params.append('lostPetStatus', lostPetStatus);
    }
    
    const response = await api.get<ApiResponse<PaginatedResponse<LostPet>>>(
      `${config.endpoints.admin.lostPets}?${params.toString()}`
    );
    return response.data.data;
  },

  getLostPetStats: async (): Promise<LostPetStats> => {
    const response = await api.get<ApiResponse<{
      approvalStats: {
        waitingApproval: number;
        approved: number;
        rejected: number;
        total: number;
      };
      lostPetStatusStats: {
        active: number;
        found: number;
        cancelled: number;
        total: number;
      };
    }>>(
      `${config.endpoints.admin.lostPets}/admin/stats`
    );
    
    const data = response.data.data;
    return {
      total: data.lostPetStatusStats.total,
      active: data.lostPetStatusStats.active,
      found: data.lostPetStatusStats.found,
      cancelled: data.lostPetStatusStats.cancelled,
      waitingApproval: data.approvalStats.waitingApproval,
      approved: data.approvalStats.approved,
      rejected: data.approvalStats.rejected,
    };
  },

  getLostPetById: async (id: string): Promise<LostPet> => {
    const response = await api.get<ApiResponse<{ lostPet: LostPet }>>(
      `${config.endpoints.admin.lostPets}/${id}`
    );
    return response.data.data.lostPet;
  },
};

// Export apiService for advanced usage
export { apiService } from './apiService';

export default api;
